import { useState } from "react";
import { ListChecks, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import AdminOnboardingSimulator from "@/components/onboarding/AdminOnboardingSimulator";
import AdminPlanSwitcher from "@/components/plan/AdminPlanSwitcher";
import AdminAssistantSwitcher from "@/components/plan/AdminAssistantSwitcher";
import AdminWelcomeTourButton from "@/components/plan/AdminWelcomeTourButton";
import PlanHeaderStatus from "@/components/plan/PlanHeaderStatus";
import UpgradePlansMenu from "@/components/plan/UpgradePlansMenu";
import PanelAudioToggle from "@/components/plan/PanelAudioToggle";
import { usePlanUi } from "@/hooks/usePlanUi";

interface AdminHeaderControlsProps {
  onBackOffice: () => void;
}

/** Una sola barra recorrible con todos los accesos exclusivos del administrador. */
export default function AdminHeaderControls({ onBackOffice }: AdminHeaderControlsProps) {
  const { lang } = useLanguage();
  const [introOpen, setIntroOpen] = useState(false);
  const es = lang === "es";
  const planUi = usePlanUi();
  const controlClass = "h-9 shrink-0 rounded-full border border-border bg-card px-3 text-xs text-foreground shadow-none hover:bg-secondary";

  return (
    <>
      <div
        className="flex min-w-0 flex-1 items-center gap-1.5 border-0 bg-transparent p-0 text-foreground overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&_span]:!text-foreground [&_select]:!text-foreground [&_button:not([aria-pressed=true])]:!text-foreground [&_svg]:!text-foreground"
        aria-label={es ? "Controles de administrador" : "Administrator controls"}
      >
        <Button type="button" size="sm" variant="outline" className={controlClass} onClick={onBackOffice}>
          <Settings aria-hidden="true" />
          <span>{es ? "Back Office" : "Back Office"}</span>
        </Button>
        <Button type="button" size="sm" variant="outline" className={controlClass} onClick={() => setIntroOpen(true)}>
          <ListChecks aria-hidden="true" />
          <span>Intro</span>
        </Button>
        <AdminWelcomeTourButton className={controlClass} />
        <AdminPlanSwitcher />
        <AdminAssistantSwitcher />
        {planUi.previewOn && (
          <div data-tour="plan-pill" className="flex shrink-0 items-center gap-1.5">
            {["basico", "club_pro", "completo"].includes(String(planUi.planKey ?? "")) && <PanelAudioToggle />}
            <PlanHeaderStatus />
            <UpgradePlansMenu />
          </div>
        )}
      </div>
      {introOpen && <AdminOnboardingSimulator onClose={() => setIntroOpen(false)} />}
    </>
  );
}